import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getSupportedDetailedTypes } from 'lib/analytics';
import { Filters } from 'components/common/filter';
import { AnalyticsPageFilters } from '.';

import type { GetCategoriesRes } from 'shared-types/responses';

const AnalyticsDetailed = () => {
  const {
    messages,
    serverConfig: {
      individualAchievementsEnabled,
      individualAchievementsDisplayForAdmins
    },
    user
  } = useReduxState();

  const router = useRouter();
  const { type } = router.query as { type: keyof typeof contentTypes };

  const { data: courseCategories, loading: courseCategoriesLoading } =
    useApiData<GetCategoriesRes>({
      onlyIf: type && type === 'courses',
      url: '/admin/categories-courses'
    });

  const { data: eventCategories, loading: eventCategoriesLoading } =
    useApiData<GetCategoriesRes>({
      onlyIf: type && type === 'calendarEvents',
      url: '/admin/categories-calendar-events'
    });

  const achievementTypes = useAchievementTypes();

  const contentTypes = getSupportedDetailedTypes({ messages });

  const itemsAll = { label: messages.commonAll, value: 'all' };

  return type ? (
    <div className="analytics-detailed">
      <AnalyticsPageFilters additionalFilterIds={[type]}>
        {!courseCategoriesLoading && !eventCategoriesLoading ? (
          <Filters
            filterId={type}
            triggerProps={{
              icon: null,
              text: contentTypes[type].labels.filterText
            }}
            {...(type === 'achievements'
              ? {
                  allowMultiple: { achievements: true },
                  customFilters: [
                    {
                      allowMultiple: true,
                      icon: 'check-circle' as const,
                      items: [
                        ...Object.keys(achievementTypes).map(
                          (achievementType) => ({
                            label: achievementTypes[achievementType].title,
                            value: achievementType
                          })
                        ),
                        ...(individualAchievementsEnabled &&
                        (individualAchievementsDisplayForAdmins ||
                          user?.roles.includes('superadmin'))
                          ? [
                              {
                                label: messages.individualAchievement,
                                value: 'individual-achievement'
                              }
                            ]
                          : [])
                      ].sort((a, b) => (a.label > b.label ? 1 : -1)),
                      field: 'achievementTypes',
                      position: 1,
                      title: messages.commonType
                    }
                  ],
                  ignoreSubItems: { achievements: true },
                  filters: { achievements: { field: 'achievements', itemsAll } }
                }
              : type === 'calendarEvents'
              ? {
                  allowMultiple: { calendarEvents: true },
                  ignoreSubItems: { calendarEvents: true },
                  filters: {
                    calendarEvents: { field: 'calendarEvents', itemsAll }
                  },
                  ...(eventCategories?.categories?.length
                    ? {
                        customFilters: [
                          {
                            allowMultiple: true,
                            icon: 'tag' as const,
                            items: eventCategories.categories.map(
                              ({ id, title }) => ({
                                label: title,
                                value: `${id}`
                              })
                            ),
                            field: 'calendarEventCategories',
                            position: 1,
                            title: messages.commonCategories
                          }
                        ]
                      }
                    : {})
                }
              : type === 'competencies'
              ? {
                  allowMultiple: {
                    competences: true,
                    competenceProfiles: true
                  },
                  filters: {
                    competences: { field: 'competencies', itemsAll },
                    competenceProfiles: { field: 'competenceProfiles' }
                  }
                }
              : type === 'courses'
              ? {
                  allowMultiple: { courses: true },
                  ignoreSubItems: { courses: true },
                  filters: { courses: { field: 'courses', itemsAll } },
                  ...(courseCategories?.categories?.length
                    ? {
                        customFilters: [
                          {
                            allowMultiple: true,
                            icon: 'tag' as const,
                            items: courseCategories.categories.map(
                              ({ id, title }) => ({
                                label: title,
                                value: `${id}`
                              })
                            ),
                            field: 'courseCategories',
                            position: 1,
                            title: messages.commonCategories
                          }
                        ]
                      }
                    : {})
                }
              : {
                  // type === 'paths'
                  allowMultiple: { paths: true },
                  ignoreSubItems: { paths: true },
                  filters: { paths: { field: 'paths', itemsAll } }
                })}
            savedFiltersPreservedKeys={['type']}
          />
        ) : null}
      </AnalyticsPageFilters>
    </div>
  ) : null;
};

export default AnalyticsDetailed;
